import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { CheckMark } from '../../../icons/CheckMark';
import { DownloadIcon } from '../../../icons/DownloadIcon';
import { getQualityScore } from '../Quality/getQualityScore/getQualityScore';
import { QualityScore } from '../Quality/Quality';
import { SmallError } from '../../../icons/SmallError';
import TestsPassed from './img-tests-passed.svg';
import { Loading } from '../../../icons/Loading';

const useStyles = makeStyles({
  resultContainer: {
    marginTop: '1em',
    '&:not(:last-child)': {
      paddingBottom: '1em',
      borderBottom: '0.1em solid #8891AA',
    },
    '@media screen and (max-height: 700px)': {
      marginTop: 'none',
    },
  },
  iconContainer: {
    display: 'flex',
    '& svg': {
      margin: '0.2em 0.8em 0 0',
    },
  },
  downloadButton: {
    marginRight: '1.5em',
    '& svg': {
      position: 'relative',
      left: '-5px',
    },
    '@media screen and (max-width: 351px)': {
      marginBottom: '10px',
    },
  },
  illustrationContainer: {
    display: 'flex',
    alignItems: 'center',
    '@media screen and (max-width: 950px)': {
      justifyContent: 'center',
      display: 'none',
    },
  },
  image: {
    width: '245px',
    height: '320px',
    '@media screen and (max-width: 950px)': {
      marginTop: '12px',
      width: '50vh',
      height: 'auto',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  customTypography: {
    '@media screen and (max-height: 700px)': {
      marginBottom: '0px',
    },
  },
  gridContainer: {
    justifyContent: 'space-between',
    '@media screen and (max-height: 950px)': {
      height: 'calc(100vh - 100px)',
      overflow: 'auto',
      paddingTop: '50px',
      paddingBottom: '5px',
    },
  },
  button: {
    background: 'white',
    '@media screen and (max-width: 351px)': {
      paddingTop: '10px',
    },
  },
  loading: {
    animation: '$loading 1.5s linear infinite',
    display: 'flex',
  },
  '@keyframes loading': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  textBox: {
    width: '340px',
    height: '48px',
    textAlign: 'center',
  },
});

export function Results() {
  const { state, downloadFinalTestResults, dispatch } = useAppStateContext();
  const { totalQualityScore } = getQualityScore(state.bitrateTestUDP.reportUDP, state.bitrateTestTCP.reportTCP);
  const classes = useStyles();
  const { userAgentInfo } = useAppStateContext();
  const browser = userAgentInfo.browser;

  const connectivityTestPassed = state.sipConnectivityUSTestConnected && state.sipConnectivitySATestConnected;
  const codecsTestsPassed = state.videoCodecTestSuccessful && state.audioCodecTestSuccessful;
  const testsPassed =
    (totalQualityScore === QualityScore.Excellent || totalQualityScore === QualityScore.Good) &&
    connectivityTestPassed &&
    codecsTestsPassed;
  const qualityScore = QualityScore[totalQualityScore].toLowerCase();
  const browserTestPassed = browser.name === 'Chrome';
  const speedTestNotFinished: boolean =
    state.downloadSpeedResult === null && state.uploadSpeedResult === null && state.bitrateTestUDP.reportUDP === null;

  return speedTestNotFinished ? (
    <Container>
      <Grid container className={classes.container}>
        <Typography variant="h1" gutterBottom>
          Hang tight!
        </Typography>
        <Typography variant="body1" gutterBottom className={classes.textBox}>
          We're just finishing up your personalized results and creating your report.
        </Typography>
        <div className={classes.loading}>
          <Loading />
        </div>
      </Grid>
    </Container>
  ) : (
    <Container>
      <Grid container className={classes.gridContainer}>
        <Grid item md={5}>
          <Typography variant="h1" paragraph>
            {testsPassed ? 'All tests passed!' : 'Some tests failed'}
          </Typography>

          {testsPassed ? (
            <Typography variant="body1" paragraph className={classes.customTypography}>
              As far as we can tell, your video should be working. If you're still experiencing issues, download your
              report results and send to net2phone support.
            </Typography>
          ) : (
            <Typography variant="body1" paragraph className={classes.customTypography}>
              <strong>One out of four </strong>
              tests failed – use this list to solve common video issues and restart the test. If you can’t easily solve
              the problem(s), download report results and send to net2phone support.
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            disableRipple
            className={classes.downloadButton}
            onClick={downloadFinalTestResults}
          >
            <DownloadIcon />
            Download report results
          </Button>
          <Button
            color="primary"
            className={classes.button}
            variant="outlined"
            disableRipple
            onClick={() => window.location.reload()}
          >
            Restart tests
          </Button>
          <Grid item md={5} className={classes.illustrationContainer}>
            {testsPassed ? <img src={TestsPassed} alt="Success" className={classes.image} /> : null}
          </Grid>
        </Grid>

        <Grid item md={5}>
          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {browserTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                Device &amp; Network Setup
              </Typography>
            </div>
            {browserTestPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Audio and video successfully received from your hardware and browser.
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Audio and video successfully received from your hardware but your browser is not supported.
              </Typography>
            )}
            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.CameraTest })}
              style={{ marginRight: '1.5em' }}
            >
              Review hardware
            </Button>
            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.BrowserTest })}
            >
              Review browser
            </Button>
          </div>

          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {connectivityTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                Connectivity
              </Typography>
            </div>

            {connectivityTestPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                All connections are working successfully.
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Transport connection attempt (or automatic re-attempt) fails.
              </Typography>
            )}

            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Connectivity })}
            >
              Review connectivity
            </Button>
          </div>

          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {codecsTestsPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                Audio/Video Codecs
              </Typography>
            </div>

            {codecsTestsPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Your browser supports audio/video codecs required by net2phone.
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Your browser does not support audio/video codecs required by net2phone.
              </Typography>
            )}

            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.CodecsTest })}
            >
              Review Audio/Video Codecs
            </Button>
          </div>

          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {testsPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                Quality &amp; Performance
              </Typography>
            </div>

            {testsPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Awesome! Your expected call quality is <strong>{qualityScore}</strong> and overall performance looks
                {qualityScore === 'excellent' ? ' good' : ' ok'}.
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                Your overall score is <strong>{qualityScore}</strong> which means that your connection isn't good enough
                to run video properly. Try out these tips and rerun the test
              </Typography>
            )}

            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Quality })}
            >
              Review performance
            </Button>
          </div>
        </Grid>
      </Grid>
    </Container>
  );
}
